import {
  monthlyPayment,
  remainingBalance,
} from "./finance";
import {
  num,
  pct,
  safeDiv,
  type CalculatorDefinition,
  type CalculatorOutput,
} from "./types";

/**
 * Cash-on-cash return for a leveraged small-business acquisition.
 *
 * The figure that matters when you buy a boring local business with a bank or
 * seller note: what does the cash you actually wired earn, per year, after the
 * loan is serviced.
 */
export const cashOnCashCalculator: CalculatorDefinition = {
  kind: "cash-on-cash",
  slug: "cash-on-cash-return",
  name: "Cash-on-Cash Return",
  nameKo: "현금 수익률 계산기",
  summary:
    "What the cash you actually wired earns each year, after debt service — the real yield on an acquisition.",
  interpretation:
    "Cash-on-cash divides yearly pre-tax cash flow by the cash you put in. It rewards leverage, so a thin deal can look excellent at 10% down and terrible at 100% down. Always read it next to DSCR: a 40% return with a 1.05× coverage ratio is one slow quarter away from a missed payment.",
  disclaimer:
    "Estimates only, for education. Excludes taxes, capex reserves, and working-capital swings. Not investment, legal, or tax advice — underwrite a real deal with your own accountant.",
  relatedCaseStudySlugs: ["laundromat-seller-financed", "vending-route-suburban"],

  fields: [
    /* ------------------------------ The deal ------------------------------ */
    {
      id: "purchasePrice",
      label: "Purchase price",
      kind: "currency",
      group: "The deal",
      defaultValue: 285000,
      min: 0,
      step: 5000,
      help: "Agreed price for the business, excluding closing costs.",
    },
    {
      id: "downPaymentPct",
      label: "Down payment",
      kind: "percent",
      group: "The deal",
      defaultValue: 15,
      min: 0,
      max: 100,
      step: 1,
      help: "SBA 7(a) deals often land near 10–15%. Seller notes can go lower.",
    },
    {
      id: "closingCosts",
      label: "Closing costs & fees",
      kind: "currency",
      group: "The deal",
      defaultValue: 12000,
      min: 0,
      step: 500,
      help: "Legal, diligence, loan packaging, escrow. Routinely underestimated.",
    },
    {
      id: "workingCapital",
      label: "Working capital injected",
      kind: "currency",
      group: "The deal",
      defaultValue: 15000,
      min: 0,
      step: 1000,
      help: "Cash left in the business to cover the first months of payroll and inventory.",
    },

    /* -------------------------------- Debt -------------------------------- */
    {
      id: "interestRate",
      label: "Interest rate",
      kind: "percent",
      group: "Financing",
      defaultValue: 10.5,
      min: 0,
      max: 40,
      step: 0.125,
    },
    {
      id: "loanTermYears",
      label: "Loan term",
      kind: "years",
      group: "Financing",
      defaultValue: 10,
      min: 1,
      max: 30,
      step: 1,
    },

    /* ------------------------------ Operations ---------------------------- */
    {
      id: "annualRevenue",
      label: "Annual revenue",
      kind: "currency",
      group: "Operations",
      defaultValue: 190000,
      min: 0,
      step: 5000,
    },
    {
      id: "annualOperatingExpenses",
      label: "Annual operating expenses",
      kind: "currency",
      group: "Operations",
      defaultValue: 118000,
      min: 0,
      step: 5000,
      help: "Everything except loan payments — rent, utilities, labour, supplies, insurance.",
    },
    {
      id: "ownerSalary",
      label: "Owner salary taken",
      kind: "currency",
      group: "Operations",
      defaultValue: 0,
      min: 0,
      step: 1000,
      help: "If you pay yourself a wage, put it here so the return reflects what the asset earns, not what you earn.",
    },
    {
      id: "capexReservePct",
      label: "Capex reserve",
      kind: "percent",
      group: "Operations",
      defaultValue: 4,
      min: 0,
      max: 50,
      step: 0.5,
      help: "Share of revenue set aside for equipment replacement. Skipping this is the most common way these deals surprise people.",
    },
  ],

  compute: (values) => {
    const price = num(values, "purchasePrice");
    const downPct = pct(values, "downPaymentPct");
    const closing = num(values, "closingCosts");
    const working = num(values, "workingCapital");
    const rate = num(values, "interestRate");
    const term = num(values, "loanTermYears");
    const revenue = num(values, "annualRevenue");
    const opex = num(values, "annualOperatingExpenses");
    const salary = num(values, "ownerSalary");
    const capexPct = pct(values, "capexReservePct");

    const downPayment = price * downPct;
    const loanAmount = Math.max(price - downPayment, 0);
    const cashInvested = downPayment + closing + working;

    const capexReserve = revenue * capexPct;
    /** Net operating income — before debt, after the reserve and any owner wage. */
    const noi = revenue - opex - salary - capexReserve;

    const monthlyDebt = monthlyPayment(loanAmount, rate, term);
    const annualDebtService = monthlyDebt * 12;

    const annualCashFlow = noi - annualDebtService;
    const monthlyCashFlow = annualCashFlow / 12;

    const cashOnCash = safeDiv(annualCashFlow, cashInvested);
    const dscr = safeDiv(noi, annualDebtService);
    const capRate = safeDiv(noi, price);
    const payback = annualCashFlow > 0 ? safeDiv(cashInvested, annualCashFlow) : null;

    // Principal retired in year one is a real return that cash-on-cash misses.
    const principalYearOne =
      loanAmount - remainingBalance(loanAmount, rate, term, 12);
    const totalReturnYearOne = safeDiv(
      annualCashFlow + principalYearOne,
      cashInvested,
    );

    const outputs: CalculatorOutput[] = [
      {
        id: "cashOnCash",
        label: "Cash-on-cash return",
        value: (cashOnCash ?? 0) * 100,
        format: "percent",
        tone: annualCashFlow >= 0 ? "currency" : "risk",
        primary: true,
        note:
          cashOnCash == null
            ? "Enter the cash you are putting in."
            : cashOnCash >= 0.2
              ? "Strong on paper — pressure-test the revenue before believing it."
              : cashOnCash >= 0.1
                ? "In the normal band for a stabilised small business."
                : cashOnCash >= 0
                  ? "Thin. Ask what this beats after your own hours are priced in."
                  : "Negative. The deal does not service itself as modelled.",
      },
      {
        id: "annualCashFlow",
        label: "Annual cash flow",
        value: annualCashFlow,
        format: "currency",
        tone: annualCashFlow >= 0 ? "currency" : "risk",
        primary: true,
      },
      {
        id: "dscr",
        label: "Debt service coverage",
        value: dscr ?? 0,
        format: "multiple",
        tone: dscr == null ? "neutral" : dscr >= 1.25 ? "currency" : "risk",
        primary: true,
        note:
          dscr == null
            ? "No debt modelled."
            : dscr >= 1.5
              ? "Comfortable cushion."
              : dscr >= 1.25
                ? "Around the level most SBA lenders want to see."
                : "Below typical lender minimums — expect financing pushback.",
      },
      {
        id: "cashInvested",
        label: "Total cash in",
        value: cashInvested,
        format: "currency",
        tone: "cost",
        note: "Down payment plus closing costs plus working capital.",
      },
      {
        id: "loanAmount",
        label: "Amount financed",
        value: loanAmount,
        format: "currency",
        tone: "neutral",
      },
      {
        id: "monthlyDebtService",
        label: "Monthly loan payment",
        value: monthlyDebt,
        format: "currency-exact",
        tone: "cost",
      },
      {
        id: "noi",
        label: "Net operating income",
        value: noi,
        format: "currency",
        tone: noi >= 0 ? "currency" : "risk",
        note: "After operating expenses, owner salary, and capex reserve.",
      },
      {
        id: "monthlyCashFlow",
        label: "Monthly cash flow",
        value: monthlyCashFlow,
        format: "currency",
        tone: monthlyCashFlow >= 0 ? "currency" : "risk",
      },
      {
        id: "capRate",
        label: "Unlevered yield",
        value: (capRate ?? 0) * 100,
        format: "percent",
        tone: "neutral",
        note: "What the business returns before any financing — the honest comparison across deals.",
      },
      {
        id: "totalReturnYearOne",
        label: "Year-one return incl. principal",
        value: (totalReturnYearOne ?? 0) * 100,
        format: "percent",
        tone: "neutral",
        note: "Cash flow plus the loan principal you retired in year one.",
      },
      {
        id: "paybackYears",
        label: "Payback period",
        value: payback ?? 0,
        format: "number-1dp",
        tone: "neutral",
        note:
          payback == null
            ? "No payback while cash flow is negative."
            : "Years for cash flow to return your invested cash.",
      },
    ];

    const warnings: string[] = [];
    if (cashInvested <= 0) {
      warnings.push(
        "No cash invested, so cash-on-cash is undefined. A true zero-down deal is measured by coverage ratio instead.",
      );
    }
    if (dscr != null && dscr < 1) {
      warnings.push(
        "Net operating income does not cover the loan payment. As modelled, this deal needs outside cash to stay current.",
      );
    }
    if (capexPct === 0 && revenue > 0) {
      warnings.push(
        "No capex reserve set. Equipment-heavy businesses — laundromats, vending, trucking — will eventually bill you for this.",
      );
    }
    if (salary === 0 && revenue > 0) {
      warnings.push(
        "No owner salary modelled. If you work in the business, this return includes payment for your labour.",
      );
    }

    return { outputs, warnings };
  },
};
